/**
 * @fileoverview Componente responsável por comparar o volume de mensagens por usuário no dashboard.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Typography from "@material-ui/core/Typography";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import api from '../../services/api';
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import './button.css';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.padding,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(2),
    }
}));

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    Filler
);

// Ajuda deste gráfico e da pizza "IA x Humano" no Dashboard e no GeneralDashboard,
// em linguagem simples. A regra completa é a de sentMessagesQuery
// (MessagesPerUserService, backend): cada mensagem conta para quem a enviou; sem
// essa informação, para quem está com o ticket; a da IA nunca conta para pessoa.
export const MESSAGES_PER_USER_HELP =
    "Quantas mensagens cada pessoa e cada IA enviou no período. " +
    "Disparo em massa conta para quem criou o disparo. " +
    "Não conta mensagens recebidas nem de grupos.";

export const AI_VS_HUMAN_HELP =
    "Mensagens enviadas nas últimas 24 horas, separadas entre as que a IA mandou e as que pessoas mandaram. " +
    "Não conta mensagens recebidas nem de grupos.";

export const ChartsMessagesPerUser = ({ initialDate, finalDate, last24h, hourFrom, hourTo, groupName, departmentIds }) => {
    const theme = useTheme();
    const isDark = theme.palette.type === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [messagesData, setMessagesData] = useState({ data: [] });
    const [hoveredDataset, setHoveredDataset] = useState(null);
    // Usuário isolado por clique na legenda (null = todos). Serve para medir
    // um atendente sozinho no período — com todas as linhas juntas, quem tem
    // volume baixo fica esmagado pela escala de quem tem volume alto.
    //
    // Guardado pelo NOME, não pela posição: ao trocar o período a lista de
    // usuários muda, e um índice apontaria para outra pessoa sem avisar.
    const [selectedUser, setSelectedUser] = useState(null);
    const chartRef = useRef(null);
    // Só a resposta da ÚLTIMA consulta vale. Sem isto, clicar 30d e logo 7d
    // deixava a resposta lenta de 30 dias sobrescrever a de 7: a legenda
    // mostrava o total de 30 dias com o chip 7d aceso.
    const fetchSeqRef = useRef(0);

    const companyId = localStorage.getItem("companyId");


    useEffect(() => {
        if (initialDate && finalDate) {
        handleGetMessagesInformation();
        }
    }, [initialDate, finalDate, last24h, hourFrom, hourTo, groupName, departmentIds]);

    const getChartOptions = (theme, hoveredIndex) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            datalabels: {
                display: false,
            },
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: true,
                backgroundColor: isDark ? 'rgba(15,23,42,0.94)' : 'rgba(15,23,42,0.92)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.16)',
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                callbacks: {
                    label: function(context) {
                        return `${context.dataset.label}: ${context.parsed.y}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.08)'
                },
                ticks: {
                    color: isDark ? '#E0E0E0' : '#64748b',
                    font: {
                        family: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
                        size: 11
                    }
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    display: true,
                    color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.08)'
                },
                ticks: {
                    color: isDark ? '#E0E0E0' : '#64748b',
                    font: {
                        family: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
                        size: 11
                    }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'nearest'
        },
        onHover: (event, elements) => {
            if (elements && elements.length > 0) {
                const datasetIndex = elements[0].datasetIndex;
                setHoveredDataset(datasetIndex);
            } else {
                setHoveredDataset(null);
            }
        },
        hover: {
            mode: 'nearest',
            intersect: false
        }
    });

    // Função para gerar cores dinamicamente
    const generateColors = (count) => {
        const baseColors = [
            '#4A90E2', // Azul principal
            '#7ED321', // Verde
            '#F5A623', // Laranja
            '#D0021B', // Vermelho
            '#9013FE', // Roxo
            '#50E3C2', // Turquesa
            '#B8E986', // Verde claro
            '#4A4A4A', // Cinza escuro
            '#BD10E0', // Magenta
            '#FF6B6B', // Vermelho coral
            '#4ECDC4', // Turquesa claro
            '#45B7D1', // Azul claro
            '#96CEB4', // Verde menta
            '#FFEAA7', // Amarelo claro
            '#DDA0DD', // Ameixa
            '#98D8C8', // Verde água
            '#F7DC6F', // Amarelo dourado
            '#BB8FCE', // Lavanda
            '#85C1E9', // Azul bebê
            '#F8C471'  // Laranja claro
        ];

        if (count <= baseColors.length) {
            return baseColors.slice(0, count);
        }

        // Se precisar de mais cores, gera cores adicionais usando HSL
        const colors = [...baseColors];
        const hueStep = 360 / count;

        for (let i = baseColors.length; i < count; i++) {
            const hue = (i * hueStep) % 360;
            const saturation = 60 + (i % 3) * 10; // Varia entre 60-80%
            const lightness = 50 + (i % 2) * 10;  // Varia entre 50-60%
            colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
        }

        return colors;
    };

    // Função para gerar dados baseados no período filtrado
    const generateChartData = () => {
        if (!messagesData?.data || messagesData.data.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        const isSameDay = initialDate === finalDate;

        let labels = [];
        let datasets = [];

        // Pegar todos os usuários únicos
        const users = [...new Set(messagesData.data.map(item => item.nome))];
        const colors = generateColors(users.length);

        if (last24h || isSameDay) {
            // Relatório por horários (mesmo dia)
            let hoursCount = 24;
            let startMoment = null;
            if (last24h && hourFrom && hourTo) {
                startMoment = new Date(`${initialDate}T${hourFrom}`);
                startMoment.setMinutes(0, 0, 0);
                const endMoment = new Date(`${finalDate}T${hourTo}`);
                endMoment.setMinutes(0, 0, 0);
                hoursCount = Math.floor((endMoment.getTime() - startMoment.getTime()) / (60 * 60 * 1000)) + 1;
            } else {
                const todayStr = new Date().toISOString().slice(0, 10);
                const isToday = initialDate === todayStr;
                const currentHour = new Date().getHours();
                hoursCount = isToday ? currentHour + 1 : 24;
            }

            labels = Array.from({ length: hoursCount }, (_, i) => {
                if (startMoment) {
                    const d = new Date(startMoment.getTime() + i * 60 * 60 * 1000);
                    return `${String(d.getHours()).padStart(2, '0')}h`;
                }
                return `${i.toString().padStart(2, '0')}h`;
            });

            datasets = users.map((user, userIndex) => {
                const data = new Array(hoursCount).fill(0);

                messagesData.data.forEach(item => {
                    if (item.nome === user) {
                        if (last24h && item.hour_index !== undefined) {
                            const hourIndex = parseInt(item.hour_index);
                            if (hourIndex >= 0 && hourIndex < hoursCount) {
                                data[hourIndex] = parseInt(item.quantidade) || 0;
                            }
                        } else if (item.horario !== undefined) {
                            const hour = parseInt(item.horario);
                            if (hour >= 0 && hour < hoursCount) {
                                data[hour] = parseInt(item.quantidade) || 0;
                            }
                        }
                    }
                });

                // Mesmo isolamento do modo por dia — este ramo atende
                // "últimas 24h" e dia único, e sem isto o clique na legenda
                // não teria efeito nessas visões.
                const hasSelection = selectedUser !== null && users.includes(selectedUser);
                const isHovered = hasSelection
                    ? selectedUser === user
                    : hoveredDataset === userIndex;
                const isOtherHovered = hasSelection
                    ? false
                    : (hoveredDataset !== null && hoveredDataset !== userIndex);
                const opacity = isOtherHovered ? 0.1 : 1;

                return {
                    label: user,
                    hidden: hasSelection && selectedUser !== user,
                    data,
                    borderColor: isOtherHovered ? colors[userIndex] + '20' : colors[userIndex],
                    backgroundColor: colors[userIndex] + (isHovered ? '40' : '15'),
                    borderWidth: isHovered ? 3 : 2,
                    pointRadius: isHovered ? 4 : 0,
                    pointHoverRadius: 6,
                    pointBackgroundColor: colors[userIndex],
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    tension: 0.4,
                    fill: true,
                    order: isHovered ? 0 : userIndex + 1
                };
            });
        } else {
            // Relatório por dias (período)
            const [startYear, startMonth, startDay] = initialDate.split('-').map(Number);
            const [endYear, endMonth, endDay] = finalDate.split('-').map(Number);

            const currentDate = new Date(startYear, startMonth - 1, startDay);
            const endDate = new Date(endYear, endMonth - 1, endDay);

            while (currentDate <= endDate) {
                const day = String(currentDate.getDate()).padStart(2, '0');
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const year = currentDate.getFullYear();
                const formattedDate = `${day}/${month}/${year}`;

                labels.push(formattedDate);
                currentDate.setDate(currentDate.getDate() + 1);
            }

            datasets = users.map((user, userIndex) => {
                const data = new Array(labels.length).fill(0);

                messagesData.data.forEach(item => {
                    if (item.nome === user && item.data) {
                        const dayIndex = labels.findIndex(label => label === item.data);
                        if (dayIndex !== -1) {
                            data[dayIndex] = parseInt(item.quantidade) || 0;
                        }
                    }
                });

                // Com um usuário isolado o hover é ignorado no estilo: passar o
                // mouse em OUTRO item da legenda deixaria a única linha visível
                // quase transparente (isOtherHovered aplica alpha '20').
                // Seleção só vale se o usuário existe no período atual; senão
                // esconderia TODAS as linhas e o gráfico ficaria vazio.
                const temSelecao = selectedUser !== null && users.includes(selectedUser);
                const isHovered = temSelecao
                    ? selectedUser === user
                    : hoveredDataset === userIndex;
                const isOtherHovered = temSelecao
                    ? false
                    : (hoveredDataset !== null && hoveredDataset !== userIndex);
                // `hidden` em vez de filtrar o array: mantém os índices
                // estáveis (a legenda referencia userIndex) e o Chart.js
                // reescala o eixo Y só pelo que está visível — que é o ponto
                // de isolar alguém.
                const isHiddenBySelection = temSelecao && selectedUser !== user;

                return {
                    label: user,
                    hidden: isHiddenBySelection,
                    data,
                    borderColor: isOtherHovered ? colors[userIndex] + '20' : colors[userIndex],
                    backgroundColor: colors[userIndex] + (isHovered ? '40' : '15'),
                    borderWidth: isHovered ? 3 : 2,
                    pointRadius: isHovered ? 4 : 0,
                    pointHoverRadius: 6,
                    pointBackgroundColor: colors[userIndex],
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    tension: 0.4,
                    fill: true,
                    order: isHovered ? 0 : userIndex + 1
                };
            });
        }

        return { labels, datasets };
    };

    const dataCharts = generateChartData();

    const handleGetMessagesInformation = async () => {
        const seq = ++fetchSeqRef.current;
        try {
            const params = new URLSearchParams({
                initialDate,
                finalDate,
                companyId
            });
            if (last24h && hourFrom && hourTo) {
                params.append('hour_from', hourFrom);
                params.append('hour_to', hourTo);
            }
            if (groupName) {
                params.append('groupName', groupName);
            }
            if (departmentIds) {
                params.append('departmentIds', departmentIds);
            }
            const { data } = await api.get(`/dashboard/messagesPerUser?${params.toString()}`);
            if (seq !== fetchSeqRef.current) return;
            setMessagesData(data);
        } catch (error) {
            if (seq !== fetchSeqRef.current) return;
            toast.error(i18n.t("dashboard.toasts.messagesPerUserError") || "Erro ao carregar mensagens por usuário");
        }
    }

    // Função para destacar linha ao passar mouse na legenda
    const handleLegendHover = (index) => {
        setHoveredDataset(index);
    };

    const handleLegendLeave = () => {
        setHoveredDataset(null);
    };

    // Clique isola o usuário; clicar de novo no mesmo volta a mostrar todos.
    const handleLegendClick = (name) => {
        setSelectedUser(current => (current === name ? null : name));
    };

    // Renderizar legenda no container externo
    React.useEffect(() => {
        const legendContainer = document.getElementById('chart-legend');
        if (legendContainer && messagesData?.data && messagesData.data.length > 0) {
            // Pegar usuários únicos e calcular totais
            const users = [...new Set(messagesData.data.map(item => item.nome))];
            const colors = generateColors(users.length);

            // Calcular total por usuário + capturar breakdown (modo grupo).
            // Em modo single-company, breakdown vem undefined — tooltip não aparece.
            const userTotals = users.map((user, originalIndex) => {
                const items = messagesData.data.filter(item => item.nome === user);
                const total = items.reduce((sum, item) => sum + (parseInt(item.quantidade) || 0), 0);
                const itemWithBreakdown = items.find(it => Array.isArray(it.breakdown) && it.breakdown.length > 1);
                const breakdown = itemWithBreakdown ? itemWithBreakdown.breakdown : null;
                return { nome: user, total, originalIndex, breakdown };
            });

            // Ordenar por quantidade (quem mais enviou primeiro)
            userTotals.sort((a, b) => b.total - a.total);

            const grandTotal = userTotals.reduce((sum, user) => sum + user.total, 0);

            // Cores baseadas no tema
            const borderColor = isDark ? '#404040' : 'rgba(15,23,42,0.08)';
            const totalColor = isDark ? '#FFFFFF' : '#0f172a';
            const labelColor = isDark ? '#E0E0E0' : '#475569';
            const valueColor = isDark ? '#FFFFFF' : '#0f172a';
            const hoverBg = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(59,130,246,0.08)';

            legendContainer.innerHTML = `
                <div style="
                    display: flex;
                    align-items: center;
                    margin-bottom: 8px;
                    padding: 4px 0;
                    border-bottom: 1px solid ${borderColor};
                ">
                    <span style="
                        color: ${totalColor};
                        font-weight: 600;
                        font-size: 14px;
                    ">Total: ${grandTotal}</span>
                </div>
                ${userTotals.map((user) => {
                    const isSelected = selectedUser === user.nome;
                    const hasSelection = selectedUser !== null && users.includes(selectedUser);
                    // Com alguém isolado, a seleção manda no visual; sem
                    // seleção, vale o realce por hover, como antes.
                    const isHovered = isSelected || (!hasSelection && hoveredDataset === user.originalIndex);
                    const isOtherHovered = hasSelection
                        ? !isSelected
                        : (hoveredDataset !== null && hoveredDataset !== user.originalIndex);
                    const hasBreakdown = Array.isArray(user.breakdown) && user.breakdown.length > 1;
                    // Tooltip CSS puro (sem delay ~700ms do title nativo do
                    // browser). data-tooltip vira pseudo-elemento ::after no
                    // hover. Aparece instantâneo.
                    const tooltipParts = hasBreakdown
                        ? user.breakdown.map(b => `${String(b.companyName ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}: ${b.quantidade}`).join(' • ') + ' = ' + user.total
                        : '';
                    const dataTooltipAttr = hasBreakdown
                        ? `data-tooltip="${tooltipParts.replace(/"/g, '&quot;')}"`
                        : '';
                    const nameStyle = hasBreakdown
                        ? 'text-decoration: underline dotted; cursor: help;'
                        : '';
                    return `
                    <div
                        data-index="${user.originalIndex}"
                        class="legend-item ${hasBreakdown ? 'legend-item--has-tooltip' : ''}"
                        ${dataTooltipAttr}
                        style="
                            display: flex;
                            align-items: center;
                            margin-bottom: 4px;
                            padding: 4px 6px;
                            border-radius: 6px;
                            cursor: pointer;
                            transition: all 0.2s ease;
                            background-color: ${isHovered ? hoverBg : 'transparent'};
                            opacity: ${isOtherHovered ? '0.3' : '1'};
                        "
                    >
                        <div style="
                            width: 8px;
                            height: 8px;
                            background-color: ${colors[user.originalIndex]};
                            border-radius: 50%;
                            margin-right: 8px;
                            flex-shrink: 0;
                        "></div>
                        <span style="
                            color: ${isHovered ? totalColor : labelColor};
                            font-size: 12px;
                            flex: 1;
                            font-weight: ${isHovered ? '600' : '400'};
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            ${nameStyle}
                        ">${String(user.nome ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>
                        <span style="
                            color: ${valueColor};
                            font-weight: 600;
                            font-size: 12px;
                            margin-left: 8px;
                        ">${user.total}</span>
                    </div>
                `}).join('')}
            `;

            // CSS injetado uma vez por container — habilita tooltip
            // instantâneo via pseudo-elemento ::after (sem delay do
            // `title` nativo do browser).
            if (!legendContainer.querySelector('style[data-tooltip-css]')) {
              const style = document.createElement('style');
              style.setAttribute('data-tooltip-css', 'true');
              style.textContent = `
                .legend-item--has-tooltip { position: relative; }
                .legend-item--has-tooltip::after {
                  content: attr(data-tooltip);
                  position: absolute;
                  bottom: calc(100% + 6px);
                  left: 0;
                  background: ${isDark ? '#0a0a0a' : '#1e293b'};
                  color: #f8fafc;
                  border: 1px solid ${isDark ? 'rgba(91,124,255,0.3)' : 'rgba(0,0,0,0.1)'};
                  padding: 6px 10px;
                  border-radius: 6px;
                  font-size: 11px;
                  font-weight: 500;
                  white-space: nowrap;
                  opacity: 0;
                  pointer-events: none;
                  transform: translateY(4px);
                  transition: opacity 120ms ease, transform 120ms ease;
                  z-index: 9999;
                  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
                }
                .legend-item--has-tooltip:hover::after {
                  opacity: 1;
                  transform: translateY(0);
                }
              `;
              legendContainer.appendChild(style);
            }
            // Adicionar event listeners aos itens da legenda
            const items = legendContainer.querySelectorAll('.legend-item');
            items.forEach(item => {
                const index = parseInt(item.dataset.index);
                item.addEventListener('mouseenter', () => handleLegendHover(index));
                item.addEventListener('mouseleave', handleLegendLeave);
                // Resolve índice -> nome com a MESMA lista que gerou a legenda.
                item.addEventListener('click', () => handleLegendClick(users[index]));
            });
        }
    }, [messagesData, theme, hoveredDataset, selectedUser]);

    return (
        <div
            style={{ height: isMobile ? '140px' : '160px', width: '100%' }}
            onMouseLeave={() => setHoveredDataset(null)}
        >
            <Line
                ref={chartRef}
                options={getChartOptions(theme, hoveredDataset)}
                data={dataCharts}
            />
        </div>
    );
}
